import { can } from "modules/settings/accessRoles";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  ButtonGroup,
  CircularProgress as MuiCircularProgress,
  Divider as MuiDivider,
  Grid,
  Paper as MuiPaper,
  Snackbar,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getButtonGroupStyles } from "../../../utils/buttonGroupStyles";
import MuiAlert from "@mui/material/Alert";
import { spacing } from "@mui/system";
// Redux Components
import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { EditableTable } from "../../../components/EditableTable";
import { logout } from "../../user/api/actions";
import UpdateWorkOrderForm from "./UpdateWorkOrderForm";
import CreateWorkOrderForm from "./CreateWorkOrderForm";
import { setSnackbar } from "../../snackbar/api/snackbar"
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { SettingsRemoteSharp } from "@mui/icons-material";
import { updateWorkOrder } from "../api/actions";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ButtonGroupSelect from "../../../components/ButtonGroupSelect";
import { formatWorkOrderAddress } from "../utils/formatWorkOrderAddress";

const Button = styled(MuiButton)(spacing);

const DashboardToolBarButton = styled(Button)`
  padding: 4px;
  min-width: 0;
  svg {
    width: 2em;
    height: 2em;
  }
`;

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const CircularProgress = styled(MuiCircularProgress)(spacing);

const useStyles = makeStyles((theme) => ({
  title: {
    flexGrow: 1,
  },
  percentDif: {
    textAlign: "right",
    color: theme.palette.success.main,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.common.white,
  },
  loadingIcon: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
  // Use design system ButtonGroup styles
  ...getButtonGroupStyles(theme)
}));

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Spacer = styled.div`
  flex: 1 1 100%;
`;



//Editable Table Object Expects: {objectArray, fieldTitles(Array), fieldTypes(Array), fieldNames(Array), handleRowClickParentMethod}
// Method can take line items if they exist initially or starts as an empty array of nothing passed
function WorkOrdersTable(props) {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const navTo = (page) => navigate(page);
  const fieldTitles = [
    "ID",
    "Title",
    "Assigned To",
    "Created By",
    "Property",
    "Address",
    "Status",
    "Due Date",
    "Created",
    "Updated",
  ];
  const fieldTypes = [
    "label",
    "label",
    "label",
    "label",
    "label",
    "label",
    "workOrderStatus",
    "dateLabelDueDate",
    "dateLabel",
    "dateLabel",
  ];
  const fieldNames = [
    "id",
    "workOrderTitle",
    "assignedTo",
    "creator",
    "name",
    "serviceAddress",
    "description",
    "dueDate",
    "created",
    "updated",
  ]; // Must be the exact name of the properties you wanna show

  const [open, setOpen] = useState(false);
  const [add, setAdd] = useState(false);

  const [item, setItem] = useState({});
  const [create, setNew] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", type: "" });
  const [searched, setSearched] = React.useState("");
  const [rows, setRows] = useState(props.workOrders?.workOrders || { items: [] });
  const dispatch = useDispatch();

  const query = new URLSearchParams(location.search);
  const id = query.get('id');
  const [statusFilter, setStatusFilter] = React.useState('All');

  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    let filtered = { items: [] };

    if (newStatus != "All" && props.workOrders?.workOrders?.items) {
      filtered.items = props.workOrders.workOrders.items.filter(item => {
        return item.description == newStatus;
      });
      setRows(filtered)
    }
    else {
      setRows(props.workOrders?.workOrders || { items: [] });
    }
  };

  const onSettingSelected = (item) => {
    setNew(false);
    if (item) {
      setItem(item);
    }
    setOpen(!open);
  };

  const onCreate = () => {
    setNew(true);
    setItem({});
    setAdd(true);
  };

  const onCloseCreateModal = () => {
    setAdd(false);
    setNew(false);
    setItem({});
  };

  const onNewSettingSelected = () => {
    setNew(true);
    setItem({});
    setOpen(!open);
  };

  const onSettingUpdate = (uuid, newItem) => {
    props.updateWorkOrder(uuid, newItem).then(() => {
    }).catch(error => {
    });
  };

  const requestSearch = (searchedVal) => {
    let stringValue;
    let baseItems = props?.workOrders?.workOrders?.items || [];
    
    if (searchedVal && searchedVal.trim() !== "") {
      const filteredRows = baseItems.filter((row) => {
        for (const [key, value] of Object.entries(row)) {
          stringValue = String(value);
          if (stringValue?.toLowerCase().includes(searchedVal.toLowerCase()))
            return true;
        }
        return false;
      });
      let newSearch = {};
      newSearch.items = filteredRows;
      setRows(newSearch)
    } else {
      // If search is empty, show all items (filtered by status if applicable)
      setRows(props.workOrders?.workOrders || { items: [] });
    }
  };

  const cancelSearch = () => {
    setSearched("");
    setRows(props.workOrders?.workOrders || { items: [] })
    requestSearch(searched);
  };


  const deleteWorkOrder = async (uuid) => {
    try {
      let response = await axios.delete(`/workorder/${uuid}`)
      if (response.status == 200) {
        dispatch(setSnackbar(true, "success", `Deleted work order.`))
      }
    } catch (e) {
      dispatch(setSnackbar(true, "error", `Failed to delete work order.`));
    }
  }

  const canDeleteWorkOrders = can(props.user?.details, "deleteWorkOrders");
  const handleClose = () => {
    setAlert({ open: false });
  };

  useEffect(() => {
    setRows(props.workOrders?.workOrders || { items: [] });
    if (id && props.workOrders?.workOrders?.items) {
      let loadedItem = props.workOrders.workOrders.items.find(workOrder => {
        return workOrder.id == id;
      });

      if (loadedItem) {
        setItem(loadedItem)
        setOpen(true)
      }
    }
  }, [props.workOrders?.workOrders])

  // Handle search from parent component
  useEffect(() => {
    if (props.searchQuery !== undefined && props.workOrders?.workOrders) {
      requestSearch(props.searchQuery);
    }
  }, [props.searchQuery, props.workOrders?.workOrders])

  const rowsWithServiceAddress = useMemo(() => ({
    items: (rows?.items || []).map((row) => ({
      ...row,
      serviceAddress: formatWorkOrderAddress(row),
    })),
  }), [rows]);

  // Filter out missing records and apply filtering
  const toShowAll = { 
    items: rowsWithServiceAddress.items.filter(item => 
      item.workOrderTitle && 
      item.workOrderTitle !== null && 
      !item.workOrderTitle.toLowerCase().includes('missing')
    ),
    toUpdate: [], 
    toDelete: [] 
  };

  const Filters = () => {
    return (
      <ButtonGroup color="primary">
        <MuiButton
          onClick={() => handleStatusChange("All")}
          className={statusFilter == "All" ? classes.active : classes.inactive}
        >
          All
        </MuiButton>
        <MuiButton
          onClick={() => handleStatusChange("New")}
          className={statusFilter == "New" ? classes.active : classes.inactive}
        >
          New
        </MuiButton>
        <MuiButton
          onClick={() => handleStatusChange("Pending")}
          className={statusFilter == "Pending" ? classes.active : classes.inactive}
        >
          Pending
        </MuiButton>
        <MuiButton
          onClick={() => handleStatusChange("Closed")}
          className={statusFilter == "Closed" ? classes.active : classes.inactive}
        >
          Closed
        </MuiButton>
      </ButtonGroup>
    );
  };

  return (
    <div style={{ width: '100%', margin: 0, padding: 0 }}>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleClose}
        container={() => document.body}
      >
        <Alert onClose={handleClose} severity={alert.type}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      {/* Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <Filters />
        </div>
      </div>
      
      <EditableTable
        isLoading={props.workOrders?.isLoading || false}
        canDelete={canDeleteWorkOrders}
        manualItemDeletionMethod={deleteWorkOrder}
        canExport={true}
        canSave={false}
        readOnly={false}
        canAddItems={true}
        newItemCreationMethod={onCreate}
        objectArray={toShowAll}
        fieldTitles={fieldTitles}
        fieldTypes={fieldTypes}
        handleRowClickParentMethod={onSettingSelected}
        fieldNames={fieldNames}
        showItemNumber={false}
        defaultItemCount={10}
      />
      
      {open && (
        <UpdateWorkOrderForm
          new={create}
          item={item}
          updateItem={onSettingUpdate}
          onChange={onSettingSelected}
          show={open}
        />
      )}

      {add && (
        <CreateWorkOrderForm
          new={create}
          item={item}
          updateItem={onSettingUpdate}
          onChange={onCloseCreateModal}
          show={add}
        />
      )}
    </div>
  );
}
// Component Properties
WorkOrdersTable.propTypes = {
  // Store objects:
  user: PropTypes.object,
  apl: PropTypes.object,
  workOrders: PropTypes.object,
  billing: PropTypes.object,
  searchQuery: PropTypes.string,
};

// Component State
function WorkOrdersTableState(state) {
  return {
    user: state.user || {},
    workOrders: state.workOrders || { workOrders: { items: [] } },
    apl: state.apl || {},
    billing: state.billing || {}
  };
}

export default connect(WorkOrdersTableState, { logout, updateWorkOrder })(
  WorkOrdersTable
);
